import { IS_DEV, IS_PROD } from "@/config/constants";
import { useEventStore } from "@/event";
import GameError, { GameErrorType } from "@/libs/GameError";
import type { RuleError } from "@/types/shop";
import { OfferSubtype, type ShopItem } from "@/types/shop";
import { ClassicSkin, filterBySkinPrefix } from "@/utils/shop/skins";
import { validateOfferAttributes } from "@/utils/shop/validators";
import {
	createImmutableState,
	getImmutableStateProperty,
	setImmutableStateProperty,
} from "@/utils/state";
import { defineStore, storeToRefs } from "pinia";
import type { ComputedRef } from "vue";
import { computed } from "vue";
import useUserStore from "./useUserStore";

export type ShopStoreState = {
	items: Array<ShopItem> | null;
	activeItems: Array<ShopItem> | null;
	hiddenLeftBoxOffers: Array<string>; // Array of store_ids
};

interface ShopStore {
	items: ComputedRef<Array<ShopItem> | null>;
	activeItems: ComputedRef<Array<ShopItem> | null>;
	specialOffers: ComputedRef<Array<ShopItem> | null>;
	starterOffers: ComputedRef<Array<ShopItem> | null>;
	gemOffers: ComputedRef<Array<ShopItem> | null>;
	eventOffers: ComputedRef<Array<ShopItem> | null>;
	piggyBankOffers: ComputedRef<Array<ShopItem> | null>;
	provisionOffers: ComputedRef<Array<ShopItem> | null>;
	welcomeBackReward: ComputedRef<ShopItem | null>;
	welcomeBackOffer: ComputedRef<ShopItem | null>;
	hiddenLeftBoxOffers: ComputedRef<Array<string>>;
	$setState: (value: Array<ShopItem>) => void;
	getItemByStoreId: (id: string) => ShopItem | null;
	setItems: (items: Array<ShopItem> | null) => void;
	setHiddenLeftBoxOffers: (value: Array<string>) => void;
}

export default defineStore("shop", (): ShopStore => {
	const state = createImmutableState<ShopStoreState>({
		items: null,
		activeItems: null,
		hiddenLeftBoxOffers: [],
	});

	// Used only for save.
	// DO NOT USE IN GAME!
	const $setState = (value: Array<ShopItem>): void => {
		setImmutableStateProperty(state, "items", value);
	};

	// Items include all packs returned from the backend.
	// NOTE: This is important for event packs — we need them available for further calculations (e.g., rewards) even after they’ve already been purchased.
	const items = getImmutableStateProperty(state, "items", null);

	// Active packs available for purchase (used as the default filter for most of the shop, except event packs).
	const activeItems = computed((): Array<ShopItem> | null => {
		const list = items.value as Array<ShopItem> | null;

		if (!list?.length) return null;

		const filtered = list.filter((item: ShopItem): boolean => {
			if (!item.is_active) return false;
			if (!IS_DEV && item.retail_price <= 0) return false;
			if (item.attributes?.subtype) return false;

			const hasLimit =
				item?.num_of_purchases !== undefined &&
				item?.attributes?.limit !== undefined;
			if (hasLimit && item.num_of_purchases! >= item.attributes.limit!)
				return false;

			// Validation gate
			const errors: Array<RuleError> = validateOfferAttributes(
				item.attributes,
			);
			const isValid = errors.length === 0;

			if (!isValid && !IS_PROD) {
				// eslint-disable-next-line no-console
				console.warn(
					"[SHOP VALIDATION] invalid shop item filtered out",
					{
						store_id: item.store_id,
						template: item.attributes.template,
						errors,
					},
				);

				throw new GameError(
					GameErrorType.FE_ERROR,
					"[SHOP VALIDATION ERROR]: <b>" +
						item.store_id +
						"</b> <br>" +
						errors
							.map((error: RuleError): string => error.message)
							.join(", "),
				);
			}

			return isValid;
		});

		// NOTE: sort by high_priority first, then by start_date (descending - newest items on top)
		filtered.sort((a: ShopItem, b: ShopItem): number => {
			const p =
				(b.attributes?.high_priority ? 1 : 0) -
				(a.attributes?.high_priority ? 1 : 0);
			if (p !== 0) return p;

			return b.start_date > a.start_date
				? 1
				: b.start_date < a.start_date
					? -1
					: 0;
		});

		// eslint-disable-next-line no-console
		console.log("valid items:", filtered);

		return filtered.length > 0 ? filtered : null;
	});

	const specialOffers = computed((): Array<ShopItem> | null =>
		filterBySkinPrefix(activeItems.value, "skin_"),
	);

	const starterOffers = computed((): Array<ShopItem> | null =>
		filterBySkinPrefix(activeItems.value, "regular_starter_"),
	);

	const gemOffers = computed((): Array<ShopItem> | null => {
		const filtered = filterBySkinPrefix(activeItems.value, "regular_gem_");

		if (!filtered) return null;

		return [...filtered].sort(
			(a: ShopItem, b: ShopItem): number =>
				a.retail_price - b.retail_price,
		);
	});

	const eventOffers = computed((): Array<ShopItem> | null => {
		const eventStore = useEventStore();
		const { isActive: isEventActive } = storeToRefs(eventStore);

		if (!isEventActive.value) return null;
		return filterBySkinPrefix(activeItems.value, "event_"); // EventPassSkin + EventSkin
	});

	const piggyBankOffers = computed((): Array<ShopItem> | null =>
		filterBySkinPrefix(activeItems.value, "piggy_"),
	);

	const provisionOffers = computed((): Array<ShopItem> | null =>
		filterBySkinPrefix(items.value, "provision_"),
	);

	const welcomeBackReward = computed((): ShopItem | null => {
		if (!items.value) return null;

		return (
			(items.value as Array<ShopItem>)?.find(
				(item: ShopItem): boolean =>
					item.attributes?.subtype === OfferSubtype.WelcomeBackReward,
			) ?? null
		);
	});

	const welcomeBackOffer = computed((): ShopItem | null => {
		if (!items.value) return null;

		const foundItem = (items.value as Array<ShopItem>)?.find(
			(item: ShopItem): boolean =>
				item.attributes?.subtype === OfferSubtype.WelcomeBackOffer,
		);

		const savedExpiration = useUserStore().deeplinkWelcomeBackOfferExpires;

		if (!foundItem || !savedExpiration) return null;

		const expirationTime = new Date(savedExpiration).getTime();
		if (Date.now() >= expirationTime) {
			useUserStore().setDeeplinkWelcomeBackOfferExpires(null);
			return null;
		}

		return {
			...foundItem,
			end_date: savedExpiration,
			attributes: {
				...foundItem.attributes,
				skin: ClassicSkin.SKIN_02, // IMPORTANT: gfx color palette
			},
		};
	});

	const hiddenLeftBoxOffers = getImmutableStateProperty(
		state,
		"hiddenLeftBoxOffers",
		[],
	);

	const getItemByStoreId = (id: string): ShopItem | null => {
		if (!items.value) return null;

		// Custom modified welcome back offer
		if (welcomeBackOffer.value?.store_id === id)
			return welcomeBackOffer.value;

		return (
			(items.value as Array<ShopItem>)?.find(
				(item: ShopItem): boolean => item.store_id === id,
			) ?? null
		);
	};

	// since each getter comes from: items < activeItems < specific packages
	const setItems = (value: Array<ShopItem> | null): void => {
		setImmutableStateProperty(state, "items", value);
	};

	const setHiddenLeftBoxOffers = (value: Array<string>): void => {
		setImmutableStateProperty(state, "hiddenLeftBoxOffers", value);
	};

	return {
		items,
		activeItems,
		specialOffers,
		starterOffers,
		gemOffers,
		eventOffers,
		piggyBankOffers,
		provisionOffers,
		welcomeBackReward,
		welcomeBackOffer,
		hiddenLeftBoxOffers,
		$setState,
		getItemByStoreId,
		setItems,
		setHiddenLeftBoxOffers,
	};
});
